import {
  Input,
  Tabs,
  TabPane,
  Pagination,
  Empty,
  Spin,
} from '@douyinfe/semi-ui';
import { useRequest, useUrlParams } from '@byted/hooks';
import cls from 'classnames';
import { useEffect } from 'react';
import { IllustrationNoContent } from '@douyinfe/semi-illustrations';
import { IconSearch } from '@douyinfe/semi-icons';
import style from './index.module.scss';
import { forumPostService } from '@/api/forumPost';
import { Scope } from '@/typings/fe';
import { toastError } from '@/utils/toastError';
import { EsSearchResponseData } from '@/typings/open.platform.forum_bff/namespaces/forumpost';
import { PostItem } from '@/components/PostItem';

async function fetcher(params: Query) {
  try {
    const { scope = 'synthesize', q = '', limit = 10, page = 1 } = params;
    const res = await forumPostService.esSearch({
      Scope: scope === 'synthesize' ? '' : scope,
      Query: q,
      Limit: Math.max(limit, 0),
      Offset: Math.max((page - 1) * limit, 0),
    });

    if (res.error) {
      throw new Error(res.message);
    }
    return res.data;
  } catch (e) {
    toastError(e);
  }
  return undefined;
}

const tabs = [
  {
    name: 'synthesize',
    label: '全部',
  },
  {
    name: 'mini-app',
    label: '小程序',
  },
  {
    name: 'mini-game',
    label: '小游戏',
  },
  {
    name: 'merchant',
    label: '服务商',
  },
  {
    name: 'other',
    label: '其他开放能力',
  },
];

const map = {
  synthesize: 'TotalNum',
  'mini-app': 'MiniAppNum',
  'mini-game': 'MiniGameNum',
  merchant: 'MerchantNum',
  other: 'OtherNum',
};
type CountName =
  | 'TotalNum'
  | 'MiniAppNum'
  | 'MiniGameNum'
  | 'MerchantNum'
  | 'OtherNum';

function getTabName(
  all: EsSearchResponseData | undefined,
  tab: {
    name: string;
    label: string;
  },
) {
  const name = tab.label;
  let count = 0;
  if (tab.name === 'synthesize') {
    count = all?.TotalNum ?? 0;
  } else {
    const countKey = map[tab.name as keyof typeof map] as CountName;
    count = all?.[countKey] ?? 0;
  }
  if (count <= 999) {
    return `${name}(${count})`;
  }
  return `${name}(999+)`;
}

interface Query {
  q?: string;
  scope?: Scope;
  page?: number;
  limit?: number;
}

function useSearch() {
  const { value: query, setValue: setQuery } = useUrlParams<Query>();
  const { scope = 'synthesize', page = 1, limit = 10, q = '' } = query;

  const {
    data: all,
    run,
    loading: searching,
  } = useRequest(fetcher, {
    auto: false,
    debounceInterval: 500,
  });

  useEffect(() => {
    run({ scope, q, limit, page });
  }, []);

  const search = (params: Query) => {
    setQuery(params);

    run(params);
  };

  return {
    scope,
    page,
    limit,
    q,
    search,
    searching,
    all,
  };
}

const Search: React.FC = () => {
  const { search, page, limit, scope, all, q, searching } = useSearch();

  return (
    <div className={cls(style.container, 'mx-auto pb-8')}>
      <div className="my-8 w-full">
        <Input
          type="search"
          placeholder={'搜你想搜'}
          height={40}
          suffix={
            <IconSearch
              className="cursor-pointer"
              onClick={() => {
                search({
                  q,
                  limit,
                  page,
                  scope,
                });
              }}
            />
          }
          onEnterPress={() => {
            search({
              q,
              limit,
              page,
              scope,
            });
          }}
          value={decodeURIComponent(q)}
          onChange={val => {
            search({
              q: encodeURIComponent(val),
              limit,
              page: 1,
              scope,
            });
          }}
        />
      </div>
      <Tabs
        type="line"
        activeKey={scope}
        onTabClick={active => {
          search({
            q,
            limit,
            page: 1,
            scope: active as Scope,
          });
        }}>
        {tabs.map(tab => (
          <TabPane
            tab={getTabName(all, tab)}
            itemKey={tab.name}
            key={tab.name}
          />
        ))}
      </Tabs>
      <Spin spinning={searching}>
        {all?.List?.length ? (
          <div>
            {all?.List?.map(item => (
              <PostItem
                key={item.Id}
                post={item}
                scope={scope}
                isSearch={true}
              />
            ))}

            <div className="flex justify-center">
              <Pagination
                total={all[map[scope as Scope] as CountName]}
                pageSize={limit}
                hideOnSinglePage={true}
                currentPage={page}
                onChange={(current, size) => {
                  search({
                    q,
                    scope,
                    page: current,
                    limit: size,
                  });
                }}
              />
            </div>
          </div>
        ) : (
          <Empty
            className="h-[308px]"
            description="暂未找到相关内容，可尝试搜索其他关键字"
            image={
              <IllustrationNoContent
                className="mt-16"
                style={{ width: 200, height: 200 }}
              />
            }
          />
        )}
      </Spin>
    </div>
  );
};

export default Search;
